import LoadingV2 from 'components/LoadingV2'
import PMBSelect from 'components/PMBSelect'
import PMBYearFilter from 'components/PMBYearFilter'
import useIsMobile from 'hooks/useIsMobile'
import { useEffect, useMemo, useState } from 'react'
import Chart from 'react-apexcharts'
import { BarChart2 } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { Card, CardBody, Col, Row } from 'reactstrap'
import styled from 'styled-components'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import { useFetchAnnualCapacityQuery } from './annualCapacityApiSlice'
import { setFetchOption } from './annualCapacitySlice'
import BreadCrumbs from 'components/breadcrumbs'

const ScrollableContainer = styled.div`
  position: relative;
  overflow-x: scroll;
  overflow-y: hidden;
  width: 100%;
  padding: 20px 0;
  margin-bottom: 20px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    -webkit-appearance: none;
    height: ${(props) => (props.isMobile ? '6px' : '8px')};
    display: block !important;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: ${(props) => (props.isMobile ? '1px' : '5px')};
    display: block !important;
  }

  &::-webkit-scrollbar-thumb {
    background: #d4d4d4;
    border-radius: ${(props) => (props.isMobile ? '1px' : '5px')};
    display: block !important;
    min-width: 60px;
    &:hover {
      background: #c4c4c4;
    }
  }

  scrollbar-width: thin !important;
  scrollbar-color: #d4d4d4 #f1f1f1;
`

const ChartContainer = styled.div`
  min-width: ${(props) => (props.isMobile ? '800px' : '1200px')};
`

const AnnualCapacity = () => {
  const dispatch = useDispatch()
  const { tag } = useSelector((state) => state.annualCapacity)
  const isMobile = useIsMobile()

  const selectedYear = tag?.selectedYear
  const selectedRegion = tag?.selectedRegion
  const selectedSupplier = tag?.selectedSupplier

  const [regionOptions, setRegionOptions] = useState([])
  const [supplierOptions, setSupplierOptions] = useState([])

  const { data, isLoading, refetch, isFetching } = useFetchAnnualCapacityQuery(
    {
      year: selectedYear?.value || new Date().getFullYear(),
    },
    { refetchOnMountOrArgChange: true, skip: false },
  )

  const handleChangeYear = (selectedOption) => {
    if (selectedYear?.value === selectedOption?.value) {
      return
    }
    dispatch(
      setFetchOption({
        selectedYear: selectedOption,
        selectedRegion: null,
        selectedSupplier: null,
      }),
    )
  }

  const handleChangeRegion = (selectedOption) => {
    if (selectedRegion?.value === selectedOption?.value) {
      return
    }
    dispatch(
      setFetchOption({
        selectedRegion: selectedOption,
        selectedSupplier: null,
      }),
    )
  }

  const handleChangeSupplier = (selectedOption) => {
    if (selectedSupplier?.value === selectedOption?.value) {
      return
    }
    dispatch(setFetchOption({ selectedSupplier: selectedOption }))
  }

  const aggregateAllRegionsData = (regions) => {
    if (!regions || regions.length === 0) return []

    const allSuppliers = []
    regions.forEach((region) => {
      if (region.suppliers) {
        allSuppliers.push(...region.suppliers)
      }
    })

    return allSuppliers
  }

  useEffect(() => {
    if (data?.regions) {
      const regions = data.regions.map((region) => ({
        value: region.id,
        label: region.name,
        data: region,
      }))

      const allRegionOption = {
        value: 'all',
        label: 'All',
        data: {
          id: 'all',
          name: 'All',
          suppliers: aggregateAllRegionsData(data.regions),
        },
      }

      const regionsWithAll = [allRegionOption, ...regions]
      setRegionOptions(regionsWithAll)

      if (!selectedRegion) {
        dispatch(setFetchOption({ selectedRegion: allRegionOption }))
      } else if (selectedRegion.value === 'all') {
        dispatch(setFetchOption({ selectedRegion: allRegionOption }))
      } else {
        const stillExists = regionsWithAll.find(
          (opt) => opt.value === selectedRegion.value,
        )
        if (!stillExists) {
          dispatch(setFetchOption({ selectedRegion: allRegionOption }))
        } else {
          const updatedRegion = regionsWithAll.find(
            (opt) => opt.value === selectedRegion.value,
          )
          dispatch(setFetchOption({ selectedRegion: updatedRegion }))
        }
      }
    }
  }, [data, dispatch])

  useEffect(() => {
    if (selectedRegion?.data?.suppliers) {
      const suppliers = selectedRegion.data.suppliers.map((supplier) => ({
        value: supplier.id,
        label: supplier.name,
        data: supplier,
      }))

      const aggregatedData = aggregateAllSuppliersData(
        selectedRegion.data.suppliers,
      )

      const allOption = {
        value: 'all',
        label: 'All',
        data: {
          id: 'all',
          name: 'All',
          data: aggregatedData,
        },
      }

      const optionsWithAll = [allOption, ...suppliers]
      setSupplierOptions(optionsWithAll)

      if (!selectedSupplier || selectedSupplier.value === 'all') {
        dispatch(setFetchOption({ selectedSupplier: allOption }))
      } else {
        const stillExists = optionsWithAll.find(
          (opt) => opt.value === selectedSupplier.value,
        )
        if (!stillExists) {
          dispatch(setFetchOption({ selectedSupplier: allOption }))
        }
      }
    } else {
      setSupplierOptions([])
      dispatch(setFetchOption({ selectedSupplier: null }))
    }
  }, [selectedRegion, dispatch])

  const aggregateAllSuppliersData = (suppliers) => {
    if (!suppliers || suppliers.length === 0) return []

    const monthsMap = {}
    const supplierCount = suppliers.length

    suppliers.forEach((supplier) => {
      supplier.data?.forEach((monthData) => {
        if (!monthsMap[monthData.month]) {
          monthsMap[monthData.month] = {
            month: monthData.month,
            startDate: monthData.startDate,
            endDate: monthData.endDate,
            val1Label: monthData.val1Label,
            val2Label: monthData.val2Label,
            val3Label: monthData.val3Label,
            val1: 0,
            val2: 0,
            val3: 0,
          }
        }
        monthsMap[monthData.month].val1 += monthData.val1 || 0
        monthsMap[monthData.month].val2 += monthData.val2 || 0
        monthsMap[monthData.month].val3 += monthData.val3 || 0
      })
    })

    const monthOrder = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ]

    return monthOrder
      .map((month) => {
        if (!monthsMap[month]) return undefined
        return {
          ...monthsMap[month],
          val1: monthsMap[month].val1,
          val2: monthsMap[month].val2,
          val3: monthsMap[month].val3 / supplierCount,
        }
      })
      .filter((item) => item !== undefined)
  }

  const currentSupplierData = useMemo(() => {
    const rawData = selectedSupplier?.data?.data || []
    if (!rawData.length) return []

    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonth = now.getMonth() // 0-indexed

    const selectedYearValue = selectedYear?.value || currentYear

    // If viewing a future year, show no data
    if (selectedYearValue > currentYear) return []

    // If viewing current year, hide future months
    if (selectedYearValue === currentYear) {
      const monthOrder = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December',
      ]
      return rawData.filter((item) => {
        const monthIndex = monthOrder.indexOf(item.month)
        return monthIndex <= currentMonth
      })
    }

    // Past year, show all months
    return rawData
  }, [selectedSupplier, selectedYear])

  const maxVal3 = useMemo(() => {
    if (!currentSupplierData || currentSupplierData.length === 0) return 100
    const maxValue = Math.max(...currentSupplierData.map((d) => d.val3 || 0))
    return Math.max(100, Math.ceil(maxValue / 50) * 50)
  }, [currentSupplierData])

  const chartSeries = useMemo(() => {
    if (!currentSupplierData || currentSupplierData.length === 0) return []

    return [
      {
        name: currentSupplierData[0]?.val1Label || 'Total Employee',
        type: 'bar',
        data: currentSupplierData.map((month) => month.val1 || 0),
      },
      {
        name: currentSupplierData[0]?.val2Label || 'Active Employee',
        type: 'bar',
        data: currentSupplierData.map((month) => month.val2 || 0),
      },
      // Line chart for Work Capacity - temporarily hidden
      // {
      //   name: currentSupplierData[0]?.val3Label || 'Work Capacity',
      //   type: 'line',
      //   data: currentSupplierData.map((month) => month.val3 || 0),
      // },
    ]
  }, [currentSupplierData])

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: 'line',
        height: isMobile ? 400 : 500,
        zoom: { enabled: false },
        toolbar: { show: false },
      },
      plotOptions: {
        bar: {
          borderRadius: isMobile ? 1 : 5,
          horizontal: false,
          columnWidth: '60%',
          borderRadiusApplication: 'end',
        },
      },
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        markers: { shape: 'circle' },
      },
      colors: ['#3B82F6', '#10B981'], // '#F59E0B' for Work Capacity line
      dataLabels: { enabled: false },
      stroke: {
        width: [0, 0], // [0, 0, 3] when line chart enabled
        curve: 'smooth',
      },
      markers: {
        size: [0, 0], // [0, 0, 5] when line chart enabled
        hover: {
          size: [0, 0], // [0, 0, 7] when line chart enabled
        },
      },
      tooltip: {
        enabled: true,
        shared: true,
        intersect: false,
        y: [
          {
            formatter(value) {
              return newFormatThousandSeparator(value)
            },
          },
          {
            formatter(value) {
              return newFormatThousandSeparator(value)
            },
          },
          // Work Capacity tooltip formatter - temporarily hidden
          // {
          //   formatter(value) {
          //     return value.toFixed(2) + '%'
          //   },
          // },
        ],
      },
      xaxis: {
        categories:
          currentSupplierData?.map((month) => month.month.slice(0, 3)) || [],
        labels: {
          style: { fontSize: isMobile ? '10px' : '12px', colors: '#6E6E6E' },
          rotate: isMobile ? -90 : -45,
          offsetY: isMobile ? 0 : 5,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: [
        {
          title: {
            text: 'Employee Count',
            style: {
              fontSize: isMobile ? '10px' : '12px',
              color: '#6E6E6E',
            },
          },
          labels: {
            style: { fontSize: isMobile ? '10px' : '12px', colors: '#6E6E6E' },
            formatter(value) {
              return newFormatThousandSeparator(value)
            },
          },
        },
        // Work Capacity Y-axis - temporarily hidden
        // {
        //   opposite: true,
        //   title: {
        //     text: 'Work Capacity (%)',
        //     style: {
        //       fontSize: isMobile ? '10px' : '12px',
        //       color: '#F59E0B',
        //     },
        //   },
        //   min: 0,
        //   max: maxVal3,
        //   labels: {
        //     style: {
        //       fontSize: isMobile ? '10px' : '12px',
        //       colors: '#F59E0B',
        //     },
        //     formatter(value) {
        //       return value.toFixed(0) + '%'
        //     },
        //   },
        // },
      ],
      grid: {
        borderColor: '#E5E7EB',
        strokeDashArray: 4,
      },
      fill: {
        type: ['solid', 'solid'], // ['solid', 'solid', 'solid'] when line chart enabled
        opacity: [0.85, 0.85], // [0.85, 0.85, 1] when line chart enabled
      },
    }),
    [currentSupplierData, isMobile, maxVal3],
  )

  if (isLoading || isFetching) {
    return <LoadingV2 />
  }

  return (
    <div className="w-100 px-3">
      <div className="d-flex align-items-start gap-2 pt-3">
        <BreadCrumbs breadCrumbActive={'Employee Overview'} />
      </div>
      <Row className="mb-3">
        <Col sm="12" md="12" lg="12">
          <div className="d-flex flex-column mb-3">
            <h5 className="mb-2">Employee Overview</h5>
            <p className="text-muted">
              Monitor employee overview data and performance metrics
            </p>
          </div>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xs="12" md="4" className="mb-2">
          <PMBYearFilter
            value={selectedYear}
            onChange={handleChangeYear}
            label="Year"
          />
        </Col>
        <Col xs="12" md="4" className="mb-2">
          <div>
            <label
              className="mb-2"
              style={{ fontSize: '14px', fontWeight: 500 }}
            >
              Group(s)
            </label>
            <PMBSelect
              value={selectedRegion}
              onChange={handleChangeRegion}
              options={regionOptions}
              placeholder="Select Group"
              isDisabled={regionOptions.length === 0}
            />
          </div>
        </Col>
        <Col xs="12" md="4" className="mb-2">
          <div>
            <label
              className="mb-2"
              style={{ fontSize: '14px', fontWeight: 500 }}
            >
              Supplier(s)
            </label>
            <PMBSelect
              value={selectedSupplier}
              onChange={handleChangeSupplier}
              options={supplierOptions}
              placeholder="Select Supplier"
              isDisabled={supplierOptions.length === 0}
            />
          </div>
        </Col>
      </Row>

      <Card className="mb-3 border-0 shadow-sm">
        <CardBody>
          {currentSupplierData && currentSupplierData.length > 0 ? (
            <ScrollableContainer isMobile={isMobile}>
              <ChartContainer isMobile={isMobile}>
                <Chart
                  options={chartOptions}
                  series={chartSeries}
                  type="line"
                  height={isMobile ? 400 : 500}
                />
              </ChartContainer>
            </ScrollableContainer>
          ) : (
            <div className="text-center py-5">
              <BarChart2 size={48} className="text-muted mb-3" />
              <p className="text-muted mb-0">
                Tidak ada data untuk ditampilkan
              </p>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  )
}

export default AnnualCapacity
